import { useEffect, useState } from 'react'

import { DISCO_CHANGELOG_URL } from '@disco/constants/external-links'

const PRODUCT_UPDATES_LIMIT = 5
const REQUEST_TIMEOUT_MS = 8000

export type ProductUpdate = {
  id: string
  title: string
  publishedDate?: number
  excerpt: string
  url: string
  category?: string
}

type ProductUpdatesStatus = 'loading' | 'error' | 'success'

type BuilderChangelogEntry = {
  id: string
  data: {
    title?: string
    publishedDate?: number
    body?: string
    url?: string
    category?: string
    seo?: { description?: string }
  }
}

const getChangelogUrl = () => {
  const params = new URLSearchParams({
    apiKey: BUILDER_PUBLIC_API_KEY,
    limit: String(PRODUCT_UPDATES_LIMIT),
    'sort.data.publishedDate': '-1',
    // Newer entries store localised fields; without a locale, title comes back as an object
    locale: 'Default',
    fields:
      'id,data.title,data.publishedDate,data.body,data.url,data.category,data.seo.description',
  })
  return `https://cdn.builder.io/api/v3/content/changelog?${params}`
}

// Only link to disco.ac, so a bad CMS value can't send users off-site
const toChangelogEntryUrl = (path?: string) => {
  if (!path) return DISCO_CHANGELOG_URL
  try {
    const url = new URL(path, DISCO_CHANGELOG_URL)
    return url.origin === new URL(DISCO_CHANGELOG_URL).origin
      ? url.href
      : DISCO_CHANGELOG_URL
  } catch {
    return DISCO_CHANGELOG_URL
  }
}

const htmlToText = (html: string) =>
  new DOMParser().parseFromString(html, 'text/html').body.textContent?.trim() ??
  ''

const toProductUpdate = ({
  id,
  data,
}: BuilderChangelogEntry): ProductUpdate => ({
  id,
  title: data.title?.trim() ?? '',
  publishedDate: data.publishedDate,
  excerpt: data.seo?.description?.trim() || htmlToText(data.body ?? ''),
  url: toChangelogEntryUrl(data.url),
  category: data.category,
})

/** False in builds without the Builder secret (e.g. local dev, Storybook), which hide the tab. */
export const hasProductUpdatesApiKey = () => Boolean(BUILDER_PUBLIC_API_KEY)

let cachedUpdates: ProductUpdate[] | null = null

/** Test-only: the cache otherwise persists across tests in the same module. */
export const resetProductUpdatesCache = () => {
  cachedUpdates = null
}

/**
 * Fetches the latest DISCO changelog entries from Builder.io. Results are
 * cached for the session so reopening the bell popover doesn't refetch.
 */
export const useProductUpdates = () => {
  const [updates, setUpdates] = useState<ProductUpdate[]>(cachedUpdates ?? [])
  const [status, setStatus] = useState<ProductUpdatesStatus>(
    cachedUpdates ? 'success' : 'loading',
  )

  useEffect(() => {
    if (cachedUpdates) return

    const controller = new AbortController()
    let hasTimedOut = false
    const timeoutId = setTimeout(() => {
      hasTimedOut = true
      controller.abort()
    }, REQUEST_TIMEOUT_MS)

    fetch(getChangelogUrl(), { signal: controller.signal })
      .then(response => {
        if (!response.ok) throw new Error(`Builder ${response.status}`)
        return response.json()
      })
      .then(({ results }: { results: BuilderChangelogEntry[] }) => {
        cachedUpdates = results
          .map(toProductUpdate)
          .filter(({ title }) => title)
        setUpdates(cachedUpdates)
        setStatus('success')
      })
      .catch(() => {
        // An abort from unmounting isn't a failure, but one from the timeout is
        if (hasTimedOut || !controller.signal.aborted) setStatus('error')
      })
      .finally(() => clearTimeout(timeoutId))

    return () => {
      clearTimeout(timeoutId)
      controller.abort()
    }
  }, [])

  return { updates, status }
}
